import styles from './textAreaBox.module.scss'
import {
	forwardRef,
	useEffect,
	useRef,
	useImperativeHandle,
	useCallback,
} from 'react'
import HelpText from '../helpText/HelpText'
import {useState} from 'react'
import {parse} from 'path'

interface Props {
	className?: string
	autoResize?: boolean
	checkTextLength?: boolean
	maxHeightRow?: number

	showErrorBorder?: boolean
	showSuccessBorder?: boolean
	showHelpTextError?: boolean
	showHelpTextSuccess?: boolean
	resultObj?: {[key: string]: string}
	helpTextComponent?: React.ReactNode

	[key: string]: any
}
const TextAreaBox = forwardRef<HTMLTextAreaElement, Props>((props, ref) => {
	const {
		className,
		autoResize,
		checkTextLength,
		maxHeightRow,
		showErrorBorder,
		showSuccessBorder,
		showHelpTextError,
		showHelpTextSuccess,
		resultObj,
		helpTextComponent,
		...otherProps
	} = props
	// textarea ref
	const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
	// 전달된 ref가 있을 경우 textAreaRef 연결
	useImperativeHandle(ref, () => {
		if (textAreaRef.current === null) {
			throw new Error('textAreaRef is not assigned yet')
		}
		return textAreaRef.current
	})

	const maxHeight = useRef<number>(0)
	const initHeight = useRef<number>(0)
	const prevValue = useRef<string>('')
	const initialFlag = useRef<boolean>(false)
	const [valueLength, setValueLength] = useState<number>(0)

	const textareaAutoHeight = (value: string) => {
		if (textAreaRef?.current) {
			textAreaRef.current.style.height = 'auto'
			const lineHeight = window.getComputedStyle(textAreaRef.current).lineHeight
			const oneRowHeight = parseInt(lineHeight)
			const paddingTop = parseInt(
				window.getComputedStyle(textAreaRef.current).paddingTop
			)
			const paddingBottom = parseInt(
				window.getComputedStyle(textAreaRef.current).paddingBottom
			)
			const borderY = 2
			let paddingY = paddingTop + paddingBottom
			let scrollHeight = textAreaRef.current.scrollHeight
			if (!initialFlag?.current) {
				scrollHeight = scrollHeight - borderY
			}
			if (
				!initialFlag?.current &&
				(prevValue.current !== value ||
					(prevValue.current === '' && value === ''))
			) {
				initHeight.current = scrollHeight
				if (maxHeightRow && autoResize) {
					const calcMaxHeight = oneRowHeight * maxHeightRow + paddingY + borderY
					maxHeight.current = calcMaxHeight
				}
			}

			if (value) {
				if (
					maxHeightRow &&
					maxHeight.current &&
					scrollHeight + 4 > maxHeight.current
				) {
					textAreaRef.current.style.height = maxHeight.current + 'px'
				} else {
					textAreaRef.current.style.height = scrollHeight + 4 + 'px'
				}
			}

			prevValue.current = value
		}
	}

	useEffect(() => {
		if (autoResize) {
			textareaAutoHeight(otherProps?.value || '')
		}
		setValueLength(otherProps?.value?.length || 0)
	}, [otherProps?.value])

	// 결과 객체 변경 시 상태 변경
	const [currentResultObj, setCurrentResultObj] = useState(resultObj)
	useEffect(() => {
		if (resultObj && currentResultObj) {
			const check = Object.keys(resultObj).some((key) => {
				if (resultObj[key] !== currentResultObj[key]) {
					return true
				}
			})
			if (check) {
				setCurrentResultObj(resultObj)
			}
		} else {
			setCurrentResultObj(resultObj)
		}
	}, [resultObj])

	const onChangeHandler = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (!initialFlag?.current) {
				initialFlag.current = true
			}
			if (otherProps?.value === undefined) {
				if (autoResize) {
					textareaAutoHeight(e.target.value || '')
				}
				setValueLength(e.target.value.length)
			}
			otherProps?.onChange && otherProps.onChange(e)
		},
		[otherProps?.onChange]
	)

	return (
		<div className={styles[`input-textarea-box`]}>
			<textarea
				ref={textAreaRef}
				{...otherProps}
				onChange={onChangeHandler}
				className={`${autoResize ? styles['auto-resize-textarea'] : ''} 
				${styles['input-textarea']} 
				${showErrorBorder && currentResultObj?.message && currentResultObj?.result === 'error' ? styles['error'] : ''} 
              	${showSuccessBorder && currentResultObj?.message && currentResultObj?.result === 'success' ? styles['success'] : ''}
				${className ? className : ''}`}
			></textarea>
			<div className={styles['input-textarea-helptext']}>
				{checkTextLength && otherProps?.maxLength && (
					<HelpText
						className='text-area-help-text right'
						helpTextComponent={`${Math.min(valueLength || 0, otherProps.maxLength)} / ${otherProps?.maxLength}`}
					/>
				)}
				<HelpText
					resultObj={currentResultObj}
					showHelpTextError={showHelpTextError}
					showHelpTextSuccess={showHelpTextSuccess}
					helpTextComponent={helpTextComponent}
				/>
			</div>
		</div>
	)
})

export default TextAreaBox
